import { assertEvent, assign } from 'xstate5';

import {
  type DeliveryLocationWithoutNearbyStore,
  type Location,
  type LocationSearchMachineAction,
} from '../../../types';

// ─────────────────────────────────────────────────────────────────────────────

export const onSearchByDeliveryPlaceIdDone: LocationSearchMachineAction<DeliveryLocationOutput> =
  assign(({ context }, params) => ({
    focusedLocationId: params?.id,
    focusedPinId: params?.id,
    deliveryLocation: params,
    deliverySearchString: params?.address ?? context.deliverySearchString,
  }));

export const onSearchDeliveryAddressesDone: LocationSearchMachineAction<DeliveryAddressPredictionsOutput> =
  assign((_, params) => ({ deliveryAddressPredictions: params }));

export const onDeliveryFormFilled: LocationSearchMachineAction = assign(({ event }) => {
  assertEvent(event, 'SUBMIT_DELIVERY_ADDRESS_FORM');

  return { deliveryLocation: event.address };
});

export const onAddDeliveryAddressIdDone: LocationSearchMachineAction<string> = assign(
  ({ context }, params) => ({
    deliveryLocation: context.deliveryLocation
      ? { ...context.deliveryLocation, addressId: params }
      : undefined,
  }),
);

export const clearDeliveryLocation: LocationSearchMachineAction = assign({
  deliveryLocation: () => undefined,
});

export const setDeliverySearchString: LocationSearchMachineAction = assign(({ event }) => {
  assertEvent(event, 'SET_DELIVERY_SEARCH_STRING');

  return { deliverySearchString: event.searchString };
});

export const clearSelectedDeliveryAddress: LocationSearchMachineAction = assign({
  selectedDeliveryAddressPredictionDetails: () => undefined,
  selectedDeliveryAddressPredictionPlaceAddress: () => undefined,
  selectedDeliveryAddressPredictionId: () => undefined,
  selectedDeliveryAddressDetails: () => undefined,
});

export const setSelectedDeliveryAddressPredictionPlaceId: LocationSearchMachineAction = assign(
  ({ event }) => {
    assertEvent(event, 'SET_DELIVERY_PREDICTION_PLACE_ID');

    return {
      selectedDeliveryAddressPredictionPlaceAddress: event.placeAddress,
      selectedDeliveryAddressPredictionId: event.placeId,
    };
  },
);

export const setSelectedDeliveryAddressPrediction: LocationSearchMachineAction = assign(
  ({ event }) => {
    assertEvent(event, 'SET_DELIVERY_PREDICTION_DETAILS');

    return { selectedDeliveryAddressPredictionDetails: event.predictedAddress };
  },
);

export const setSelectedDeliveryAddressDetails: LocationSearchMachineAction = assign(
  ({ event }) => {
    assertEvent(event, 'SET_DELIVERY_CUSTOMER_ADDRESS');

    return { selectedDeliveryAddressDetails: event.addressDetails };
  },
);

// ─── Types ───────────────────────────────────────────────────────────────────

type DeliveryLocationOutput = Location | DeliveryLocationWithoutNearbyStore | undefined;

type DeliveryAddressPredictionsOutput =
  | {
      id: string;
      name: string;
      address: string;
      description: string;
    }[]
  | {
      id: string;
      name: string;
      address: string;
      street: string;
      city: string;
      zipCode: string;
      state: string;
      country: string;
      latitude: number;
      longitude: number;
    }[];
